import type { Theme } from "@mui/material/styles";
import type { ExpressionSpecification } from "mapbox-gl";

/**
 * Single source of truth: ordered mapping from economic zone number (1–5)
 * to its MUI palette colour key.
 */
const ZONE_COLOR_KEYS = [
  { zone: 1, key: "low" },
  { zone: 2, key: "minimal" },
  { zone: 3, key: "moderate" },
  { zone: 4, key: "significant" },
  { zone: 5, key: "substantial" },
] as const;

/**
 * Returns the MUI CSS-variable colour for an economic zone.
 * Suitable for React/MUI `sx` props and styled components.
 */
export const getEconomicZoneColor = (zone: number, theme: Theme): string => {
  const entry = ZONE_COLOR_KEYS.find((e) => e.zone === zone);
  return entry
    ? theme.vars.palette.colors.economicZone[entry.key]
    : theme.vars.palette.secondary.main;
};

/**
 * Returns a Mapbox GL `case` expression that maps the `zone` GeoJSON feature
 * property to the resolved theme colour for each economic zone.
 *
 * Mapbox expressions require concrete colour strings, so this uses
 * `theme.palette` (resolved hex values) rather than `theme.vars.palette`
 * (CSS variable references).
 */
export const economicZoneColorExpression = (
  theme: Theme,
  fallback: string,
): ExpressionSpecification => [
  "case",
  ...ZONE_COLOR_KEYS.flatMap(({ zone, key }) => [
    ["==", ["get", "zone"], zone],
    theme.palette.colors.economicZone[key],
  ]),
  fallback,
];
